"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  FluidDiagram,
  ForceAreaDiagram,
} from "@/components/tools/physics-fields/pressure-diagrams";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import type { PRESSURE_CALCULATOR_STRINGS } from "@/components/tools/pressure-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { BODIES } from "@/lib/tool-kit/physics-fields/bodies";
import { STANDARD_ATMOSPHERE } from "@/lib/tool-kit/physics-fields/constants";
import { FLUIDS, findFluid } from "@/lib/tool-kit/physics-fields/fluids";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity, typedValue } from "@/lib/tool-kit/physics-fields/input";
import {
  type ForceAreaTarget,
  type HydrostaticTarget,
  type PressureBasis,
  type PressureError,
  solveForceArea,
  solveHydrostatic,
} from "@/lib/tool-kit/physics-fields/pressure";
import {
  forceAreaWorking,
  hydrostaticWorking,
} from "@/lib/tool-kit/physics-fields/pressure-working";
import {
  ACCELERATION_UNITS,
  AREA_UNITS,
  DENSITY_UNITS,
  FORCE_UNITS,
  fromBase,
  LENGTH_UNITS,
  PRESSURE_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof PRESSURE_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

function Failure({
  error,
  strings,
}: {
  error: PressureError;
  strings: Strings;
}) {
  const messages: Record<PressureError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "below-atmosphere": strings.errorBelowAtmosphere,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

/* ── P = F / A ────────────────────────────────────────────────────────────── */

type FaKey = "P" | "F" | "A";

const FA_EXAMPLES: Record<
  string,
  { text: Record<FaKey, string>; unit: Record<FaKey, string> }
> = {
  person: {
    text: { P: "", F: "686.4655", A: "400" },
    unit: { P: "kPa", F: "N", A: "cm2" },
  },
  heel: {
    text: { P: "", F: "686.4655", A: "1" },
    unit: { P: "MPa", F: "N", A: "cm2" },
  },
  tack: {
    text: { P: "", F: "10", A: "0.1" },
    unit: { P: "MPa", F: "N", A: "mm2" },
  },
};

export function ForceAreaPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("person");
  const [target, setTarget] = useState<ForceAreaTarget>("pressure");
  const [text, setText] = useState<Record<FaKey, string>>(
    FA_EXAMPLES.person.text
  );
  const [unit, setUnit] = useState<Record<FaKey, string>>(
    FA_EXAMPLES.person.unit
  );

  const outcome = solveForceArea(
    {
      pressure: readQuantity(text.P, PRESSURE_UNITS, unit.P),
      force: readQuantity(text.F, FORCE_UNITS, unit.F),
      area: readQuantity(text.A, AREA_UNITS, unit.A),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<FaKey>[] = [
    {
      key: "P",
      label: strings.pressure,
      units: PRESSURE_UNITS,
      solved: target === "pressure",
    },
    {
      key: "F",
      label: strings.force,
      units: FORCE_UNITS,
      solved: target === "force",
    },
    {
      key: "A",
      label: strings.area,
      units: AREA_UNITS,
      solved: target === "area",
    },
  ];
  const solvedSI = {
    P: value?.pressure ?? null,
    F: value?.force ?? null,
    A: value?.area ?? null,
  };
  const names = strings.forceAreaExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = FA_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget("pressure");
    }
  };

  return (
    <>
      <SelectField
        id="pc-fa-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(FA_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="pc-fa-solve"
        onChange={(next: ForceAreaTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "pressure", label: strings.pressureShort },
          { value: "force", label: strings.forceShort },
          { value: "area", label: strings.areaShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="pc-fa"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.pressure}
                value={`${formatSig(fmt, value.pressure, sig)} Pa`}
              />
              <StatCard
                label={strings.force}
                value={`${formatSig(fmt, value.force, sig)} N`}
              />
              <StatCard
                label={strings.area}
                value={`${formatSig(fmt, value.area, sig)} m²`}
              />
              <StatCard
                label={strings.inAtmospheres}
                value={`${formatSig(fmt, value.pressure / STANDARD_ATMOSPHERE, sig)} atm`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={forceAreaWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.forceAreaCaption}>
              <ForceAreaDiagram
                description={strings.forceAreaDescription}
                id="pc-fa-diagram"
                label={strings.forceAreaLabel}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}

function enteredBasisValue(
  value: { gauge: number; absolute: number } | null,
  basis: PressureBasis
): number | null {
  if (value === null) {
    return null;
  }
  return basis === "gauge" ? value.gauge : value.absolute;
}

/* ── P = ρ g h ────────────────────────────────────────────────────────────── */

type HsKey = "P" | "d" | "g" | "h" | "s";

export function HydrostaticPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [target, setTarget] = useState<HydrostaticTarget>("pressure");
  const [basis, setBasis] = useState<PressureBasis>("gauge");
  const [fluid, setFluid] = useState("fresh-water");
  const [planet, setPlanet] = useState("earth");
  const [text, setText] = useState<Record<HsKey, string>>({
    P: "",
    d: "998.2",
    g: "9.80665",
    h: "10",
    s: "101325",
  });
  const [unit, setUnit] = useState<Record<HsKey, string>>({
    P: "kPa",
    d: "kg/m3",
    g: "m/s2",
    h: "m",
    s: "Pa",
  });

  const outcome = solveHydrostatic(
    {
      pressure: readQuantity(text.P, PRESSURE_UNITS, unit.P),
      density: readQuantity(text.d, DENSITY_UNITS, unit.d),
      gravity: readQuantity(text.g, ACCELERATION_UNITS, unit.g),
      depth: readQuantity(text.h, LENGTH_UNITS, unit.h),
      surface: readQuantity(text.s, PRESSURE_UNITS, unit.s),
    },
    target,
    basis
  );
  const value = outcome.ok ? outcome.value : null;
  const pressureLabel =
    basis === "gauge" ? strings.gaugePressure : strings.absolutePressure;
  const specs: SolveFieldSpec<HsKey>[] = [
    {
      key: "P",
      label: pressureLabel,
      units: PRESSURE_UNITS,
      solved: target === "pressure",
    },
    {
      key: "d",
      label: strings.density,
      units: DENSITY_UNITS,
      solved: target === "density",
    },
    {
      key: "g",
      label: strings.gravity,
      units: ACCELERATION_UNITS,
      solved: false,
    },
    {
      key: "h",
      label: strings.depth,
      units: LENGTH_UNITS,
      solved: target === "depth",
      hint: strings.depthHint,
    },
    {
      key: "s",
      label: strings.surface,
      units: PRESSURE_UNITS,
      solved: false,
      hint: strings.surfaceHint,
    },
  ];
  const solvedSI = {
    P: enteredBasisValue(value, basis),
    d: value?.density ?? null,
    h: value?.depth ?? null,
  };
  const pickFluid = (id: string) => {
    setFluid(id);
    const f = findFluid(id);
    if (f) {
      setText((c) => ({ ...c, d: String(f.density) }));
      setUnit((c) => ({ ...c, d: "kg/m3" }));
    }
  };
  const pickPlanet = (id: string) => {
    setPlanet(id);
    const b = BODIES.find((body) => body.id === id);
    if (b) {
      setText((c) => ({ ...c, g: String(b.gravity) }));
      setUnit((c) => ({ ...c, g: "m/s2" }));
    }
  };
  const fluidNames = strings.fluidNames as Record<string, string>;
  const planetNames = strings.planetNames as Record<string, string>;
  const perAtmosphere =
    value === null
      ? null
      : STANDARD_ATMOSPHERE / (value.density * value.gravity);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="pc-fluid"
          label={strings.fluidLabel}
          onChange={pickFluid}
          options={[
            { value: "custom", label: strings.custom },
            ...FLUIDS.map((f) => ({
              value: f.id,
              label: fluidNames[f.id] ?? f.id,
            })),
          ]}
          value={fluid}
        />
        <SelectField
          id="pc-planet"
          label={strings.planetLabel}
          onChange={pickPlanet}
          options={[
            { value: "custom", label: strings.custom },
            ...BODIES.map((b) => ({
              value: b.id,
              label: planetNames[b.id] ?? b.id,
            })),
          ]}
          value={planet}
        />
      </div>
      <PillGroup
        label={strings.solveFor}
        name="pc-hs-solve"
        onChange={(next: HydrostaticTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "pressure", label: strings.pressureShort },
          { value: "depth", label: strings.depthShort },
          { value: "density", label: strings.densityShort },
        ]}
        value={target}
      />
      <PillGroup
        label={strings.basisLabel}
        name="pc-basis"
        onChange={(next: PressureBasis) => {
          if (next === basis) {
            return;
          }
          // An entered pressure keeps its meaning: convert it to the new basis.
          const entered = readQuantity(text.P, PRESSURE_UNITS, unit.P);
          const surface = readQuantity(text.s, PRESSURE_UNITS, unit.s);
          if (target !== "pressure" && entered !== null && surface !== null) {
            const converted =
              next === "absolute" ? entered + surface : entered - surface;
            setText({
              ...text,
              P: typedValue(fromBase(PRESSURE_UNITS, unit.P, converted), sig),
            });
          }
          setBasis(next);
        }}
        options={[
          { value: "gauge", label: strings.gauge },
          { value: "absolute", label: strings.absolute },
        ]}
        value={basis}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="pc-hs"
        onText={(key, v) => {
          if (key === "d") {
            setFluid("custom");
          }
          if (key === "g") {
            setPlanet("custom");
          }
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {value === null || perAtmosphere === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.gaugePressure}
                value={`${formatSig(fmt, value.gauge, sig)} Pa`}
              />
              <StatCard
                label={strings.absolutePressure}
                value={`${formatSig(fmt, value.absolute, sig)} Pa`}
              />
              <StatCard
                label={strings.absoluteInAtmospheres}
                value={`${formatSig(fmt, value.absolute / STANDARD_ATMOSPHERE, sig)} atm`}
              />
              <StatCard
                label={strings.depth}
                value={`${formatSig(fmt, value.depth, sig)} m`}
              />
              <StatCard
                label={strings.density}
                value={`${formatSig(fmt, value.density, sig)} kg/m³`}
              />
              <StatCard
                label={strings.depthPerAtmosphere}
                value={`${formatSig(fmt, perAtmosphere, sig)} m`}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={hydrostaticWorking(
                target,
                basis,
                readQuantity(text.P, PRESSURE_UNITS, unit.P),
                value,
                fmt,
                sig
              )}
            />
            <DiagramFrame caption={strings.fluidCaption}>
              <FluidDiagram
                description={strings.fluidDescription}
                id="pc-fluid-diagram"
                label={strings.fluidDiagramLabel}
              />
            </DiagramFrame>
          </>
        )}
      </section>
    </>
  );
}
